/**
 * Backend Exporter — v1.7.4
 *
 * Routes video export through the FFmpeg backend server instead of the
 * browser-based MediaRecorder pipeline.
 *
 * Speed comparison:
 *   Browser MediaRecorder:  1× real-time  (60s video → 60s export)
 *   FFmpeg backend:        20–100× faster (60s video → 1–3s export)
 *
 * Pipeline (v1.7.0):
 *   0. Hash video (first 64 KB + size) → GET /api/upload/check  [2D: cache check]
 *      → Skip upload entirely if same video was uploaded within the last 2 hours
 *   1. Upload video blob in 10 MB chunks → POST /api/upload/chunk (×N) [2C: chunked]
 *      → Falls back to single-shot XHR if chunked upload is unavailable
 *   2. Start render job  → POST /api/render  (queued server-side) [2B: job queue]
 *   3. Watch progress    → WebSocket ws://…/ws  (10–95%)
 *   4. Download result   → GET  /api/download/:jobId  (95–100%)
 *
 * v1.7.4:
 *  - The backend origin is configurable via VITE_BACKEND_URL (see BACKEND_URL
 *    below). It used to be hardcoded to localhost, so a hosted frontend could
 *    never reach a render server and every user silently got the slow
 *    in-browser exporter.
 *  - Progress polling runs off a worker timer instead of setInterval, so a
 *    render that finishes while the tab is in the background is picked up and
 *    downloaded immediately rather than up to a minute later.
 */

import type { OverlayElement, ExportSettings } from '@/types';
import { createBackgroundInterval } from '@/utils/backgroundTicker';
import type { StopTicker } from '@/utils/backgroundTicker';

/**
 * Backend origin.
 *
 * Defaults to the local accelerator (`http://localhost:3001`) so `npm run dev`
 * and the Electron build keep working untouched. Deployments point this at a
 * hosted render server with `VITE_BACKEND_URL`, which is the difference
 * between a 1x-real-time browser export and a 20-100x faster FFmpeg one — a
 * hosted frontend could previously never reach a backend at all, so every
 * user silently fell back to the slow path.
 */
export const BACKEND_URL = (
  import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:3001'
).replace(/\/+$/, '');

/** Derive the WebSocket endpoint from the backend origin (http->ws, https->wss). */
const WS_URL = `${BACKEND_URL.replace(/^http/, 'ws')}/ws`;

// ─── 2D: Video Hash (cache fingerprint) ──────────────────────────────────────

/**
 * Compute a fast fingerprint of a video blob:
 * SHA-256 of the first 64 KB + the file size.
 *
 * This is fast (<5ms on most machines) and unique enough for cache deduplication.
 * We deliberately don't hash the full file to keep performance high.
 */
const computeVideoHash = async (blob: Blob): Promise<string> => {
  try {
    const SAMPLE_SIZE = 64 * 1024; // 64 KB
    const sample = blob.slice(0, SAMPLE_SIZE);
    const arrayBuffer = await sample.arrayBuffer();

    // Append file size as 8-byte little-endian to the sample
    const sizeBytes = new DataView(new ArrayBuffer(8));
    sizeBytes.setUint32(0, blob.size & 0xffffffff, true);
    sizeBytes.setUint32(4, Math.floor(blob.size / 0x100000000) & 0xffffffff, true);

    // Concatenate sample + size
    const combined = new Uint8Array(arrayBuffer.byteLength + 8);
    combined.set(new Uint8Array(arrayBuffer), 0);
    combined.set(new Uint8Array(sizeBytes.buffer), arrayBuffer.byteLength);

    const digest = await crypto.subtle.digest('SHA-256', combined);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Web Crypto unavailable — return empty string to skip cache
    return '';
  }
};

/**
 * Ask the backend if a video with this hash is already cached.
 * Returns the cached path if found, or null if not cached / on network error.
 */
const checkVideoCache = async (hash: string): Promise<string | null> => {
  if (!hash) return null;
  try {
    const res = await fetch(`${BACKEND_URL}/api/upload/check?hash=${encodeURIComponent(hash)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.data?.cached && data?.data?.path) {
      console.log('[BackendExporter] Cache HIT — skipping upload');
      return data.data.path as string;
    }
  } catch { /* network error — proceed with upload */ }
  return null;
};

export interface TrimRange {
  startTime: number;
  endTime: number;
  enabled: boolean;
}

export interface BackendExportOptions {
  videoBlob: Blob;
  videoName: string;
  overlays: OverlayElement[];
  /** Width of the video source (before any resolution scaling) */
  sourceWidth: number;
  sourceHeight: number;
  exportSettings: ExportSettings;
  trimRange?: TrimRange | null;
  onProgress?: (progress: number, phase: string) => void;
}

// ─── Cancellation state ─────────────────────────────────────────────────────

let _currentJobId: string | null = null;
let _currentWs: WebSocket | null = null;
let _stopPolling: StopTicker | null = null;
let _cancelled = false;

export const cancelBackendExport = async (): Promise<void> => {
  _cancelled = true;

  if (_currentWs) {
    try { _currentWs.close(); } catch { /* ignore */ }
    _currentWs = null;
  }

  if (_stopPolling) {
    _stopPolling();
    _stopPolling = null;
  }

  if (_currentJobId) {
    try {
      await fetch(`${BACKEND_URL}/api/render/${_currentJobId}`, { method: 'DELETE' });
    } catch { /* ignore */ }
    _currentJobId = null;
  }
};

// ─── Health check ────────────────────────────────────────────────────────────

/**
 * Returns true if the FFmpeg backend is reachable.
 * Times out after 2 s so the UI doesn't stall.
 */
export const checkBackendAvailable = async (): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);
    const res = await fetch(`${BACKEND_URL}/api/health`, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return false;
    // Verify this is actually our backend by checking the expected response shape
    const data = await res.json();
    return data?.status === 'ok';
  } catch {
    return false;
  }
};

// ─── Step 1: Upload (2C: chunked + 2D: cache-backed) ─────────────────────────

const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB per chunk

interface UploadResult {
  path: string;
  name: string;
  duration: number;
  width: number;
  height: number;
  fps: number;
  codec: string;
  fileSize: number;
}

/**
 * Upload one chunk to POST /api/upload/chunk.
 * Returns the assembled UploadResult when this was the final chunk, or null otherwise.
 */
const uploadChunk = async (
  blob: Blob,
  name: string,
  uploadId: string,
  chunkIndex: number,
  totalChunks: number,
  videoHash: string,
  onProgress?: (p: number, phase: string) => void
): Promise<UploadResult | null> => {
  const start = chunkIndex * CHUNK_SIZE;
  const end   = Math.min(start + CHUNK_SIZE, blob.size);
  const chunk  = blob.slice(start, end);

  const formData = new FormData();
  // Text fields MUST come before the binary chunk so that req.body is populated
  // by the time multer's diskStorage filename callback fires for the file part.
  formData.append('uploadId',    uploadId);
  formData.append('chunkIndex',  String(chunkIndex));
  formData.append('totalChunks', String(totalChunks));
  formData.append('fileName',    name);
  formData.append('videoHash',   videoHash);
  formData.append('chunk',       chunk,                   'chunk.bin');

  const res = await fetch(`${BACKEND_URL}/api/upload/chunk`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(`Chunk ${chunkIndex} upload failed: HTTP ${res.status}`);

  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? `Chunk ${chunkIndex} rejected by server`);

  // Map chunk progress to 0–10% of total progress
  const pct = Math.round(((chunkIndex + 1) / totalChunks) * 10);
  onProgress?.(pct, 'uploading');

  if (data.data?.assembled) {
    return data.data as UploadResult;
  }
  return null;
};

/**
 * Upload video in 10 MB chunks (2C).
 * Automatically falls back to single-shot upload if chunked upload fails mid-way.
 */
const uploadVideoChunked = async (
  blob: Blob,
  name: string,
  videoHash: string,
  onProgress?: (p: number, phase: string) => void
): Promise<UploadResult> => {
  const uploadId    = crypto.randomUUID();
  const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);

  console.log(`[BackendExporter] Chunked upload: ${totalChunks} chunk(s) × ${CHUNK_SIZE / 1024 / 1024} MB`);

  for (let i = 0; i < totalChunks; i++) {
    if (_cancelled) throw new Error('Export cancelled');
    const result = await uploadChunk(blob, name, uploadId, i, totalChunks, videoHash, onProgress);
    if (result) return result; // last chunk returned assembled metadata
  }

  throw new Error('Chunked upload completed but server did not return assembled result');
};

/**
 * Single-shot fallback upload via XHR (original v1.6.0 path).
 * Used when chunked upload is unavailable or the file is very small.
 */
const uploadVideoSingleShot = (
  blob: Blob,
  name: string,
  onProgress?: (p: number, phase: string) => void
): Promise<UploadResult> => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    const ext = name.endsWith('.mp4') ? 'mp4' : name.split('.').pop() ?? 'mp4';
    formData.append('video', blob, `upload.${ext}`);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/upload`);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 10);
        onProgress?.(pct, 'uploading');
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText);
          if (body.success) resolve(body.data as UploadResult);
          else reject(new Error(body.error ?? 'Upload failed'));
        } catch {
          const preview = xhr.responseText?.substring(0, 150) || '(empty)';
          reject(new Error(`Upload failed: server returned non-JSON response — ${preview}`));
        }
      } else {
        reject(new Error(`Upload failed: HTTP ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
    xhr.send(formData);
  });
};

/**
 * Smart upload: check cache first (2D), then chunked upload (2C), with single-shot fallback.
 */
const uploadVideo = async (
  blob: Blob,
  name: string,
  videoHash: string,
  onProgress?: (p: number, phase: string) => void
): Promise<UploadResult> => {
  // Use chunked upload for files > 10 MB, single-shot for small files
  if (blob.size > CHUNK_SIZE) {
    try {
      return await uploadVideoChunked(blob, name, videoHash, onProgress);
    } catch (err) {
      console.warn('[BackendExporter] Chunked upload failed, falling back to single-shot:', err);
      // Fall through to single-shot
    }
  }
  return uploadVideoSingleShot(blob, name, onProgress);
};

// ─── Polygon rasterisation ───────────────────────────────────────────────────

/**
 * Convert polygon overlays into image overlays before sending them to FFmpeg.
 *
 * FFmpeg has no polygon primitive, so ffmpegService skipped this overlay type
 * outright (`case 'polygon': continue`). Polygons therefore drew in the
 * preview and in the browser exporter but vanished from every backend export
 * — silently, with no warning anywhere. Rasterising them here to a PNG data
 * URI turns them into an overlay type the backend already renders correctly,
 * so what the user drew is what they get.
 *
 * Bounds are already in output pixels by the time this runs, so the raster is
 * generated at final resolution and needs no further scaling.
 */
const rasterizePolygonOverlays = (overlays: OverlayElement[]): OverlayElement[] =>
  overlays.map((overlay) => {
    if (overlay.type !== 'polygon') return overlay;

    const poly = overlay as any;
    const points: Array<{ x: number; y: number }> = poly.polygon?.points ?? [];
    if (points.length < 3) return overlay;

    const width = Math.max(1, Math.round(overlay.bounds.width));
    const height = Math.max(1, Math.round(overlay.bounds.height));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return overlay;

    const toCss = (c: { r: number; g: number; b: number; a: number }) =>
      `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;

    ctx.beginPath();
    points.forEach((point, i) => {
      if (i === 0) ctx.moveTo(point.x, point.y);
      else ctx.lineTo(point.x, point.y);
    });
    ctx.closePath();

    ctx.fillStyle = toCss(overlay.style.fillColor);
    ctx.fill();

    if (overlay.style.borderWidth > 0) {
      ctx.strokeStyle = toCss(overlay.style.borderColor);
      ctx.lineWidth = overlay.style.borderWidth;
      ctx.stroke();
    }

    // Opacity stays on the overlay: the backend applies it via the overlay
    // filter's alpha, so baking it into the bitmap too would apply it twice.
    return {
      ...overlay,
      type: 'image',
      image: {
        src: canvas.toDataURL('image/png'),
        originalWidth: width,
        originalHeight: height,
        preserveAspectRatio: false,
      },
    } as unknown as OverlayElement;
  });

// ─── Step 2: Start render job ────────────────────────────────────────────────

const startRenderJob = async (
  uploadResult: UploadResult,
  overlays: OverlayElement[],
  exportSettings: ExportSettings,
  trimRange: TrimRange | null | undefined
): Promise<string> => {
  const body = {
    video: {
      path: uploadResult.path,
      name: uploadResult.name,
      duration: uploadResult.duration,
      width: uploadResult.width,
      height: uploadResult.height,
      fps: uploadResult.fps,
      codec: uploadResult.codec,
      fileSize: uploadResult.fileSize,
    },
    overlays,
    exportSettings,
    trimRange: trimRange ?? null,
  };

  const res = await fetch(`${BACKEND_URL}/api/render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`Failed to start render job: HTTP ${res.status}`);
  }

  const data = await res.json();
  if (!data.success) {
    throw new Error(data.error ?? 'Render job creation failed');
  }

  return data.data.jobId as string;
};

// ─── Step 3: Watch render progress ──────────────────────────────────────────

const watchRenderProgress = (
  jobId: string,
  onProgress?: (p: number, phase: string) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    let settled = false;
    let ws: WebSocket | null = null;
    let stopPoll: StopTicker | null = null;

    const settle = (err?: Error) => {
      if (settled) return;
      settled = true;

      if (ws) { try { ws.close(); } catch { /* ignore */ } }
      if (stopPoll) { stopPoll(); stopPoll = null; }
      _currentWs = null;
      _stopPolling = null;

      if (err) reject(err);
      else resolve();
    };

    // --- Polling (always runs as a reliable fallback) ---
    const doPoll = async () => {
      if (settled) return;
      if (_cancelled) { settle(new Error('Export cancelled')); return; }

      try {
        const res = await fetch(`${BACKEND_URL}/api/render/${jobId}`);
        const data = await res.json();
        const job = data.data;

        if (job.status === 'completed') {
          onProgress?.(95, 'finalizing');
          settle();
        } else if (job.status === 'failed') {
          settle(new Error(job.error ?? 'Render failed on server'));
        } else {
          // Map 0-100 render progress to 10-93% of the total export progress
          const mapped = 10 + Math.round((job.progress ?? 0) * 0.83);
          onProgress?.(mapped, job.phase ?? 'encoding');
        }
      } catch { /* network hiccup, retry next tick */ }
    };

    // Poll from a worker timer, not setInterval: a hidden tab clamps main-thread
    // timers to >= 1 s and, after ~5 minutes, to once a minute — long enough
    // that a finished render would sit undownloaded while the user was away.
    stopPoll = createBackgroundInterval(800, () => { void doPoll(); });
    _stopPolling = stopPoll;

    // --- WebSocket (faster updates when available) ---
    try {
      ws = new WebSocket(WS_URL);
      _currentWs = ws;

      ws.onmessage = (event) => {
        if (settled) return;
        try {
          const msg = JSON.parse(event.data as string);
          if (msg.type !== 'render_progress' || msg.jobId !== jobId) return;

          const phase = msg.phase as string;
          const progress: number = msg.progress ?? 0;

          if (phase === 'completed') {
            onProgress?.(95, 'finalizing');
            settle();
          } else if (phase === 'failed') {
            settle(new Error('Render failed on server'));
          } else {
            const mapped = 10 + Math.round(progress * 0.83);
            onProgress?.(mapped, phase);
          }
        } catch { /* ignore malformed messages */ }
      };

      ws.onerror = () => { /* fall back to polling silently */ };
    } catch {
      // WebSocket not available — polling will handle it
    }
  });
};

// ─── Step 4: Download result ─────────────────────────────────────────────────

const downloadResult = async (jobId: string): Promise<Blob> => {
  const res = await fetch(`${BACKEND_URL}/api/download/${jobId}`);
  if (!res.ok) {
    throw new Error(`Download failed: HTTP ${res.status}`);
  }
  return res.blob();
};

// ─── Main export function ────────────────────────────────────────────────────

/**
 * Export a video with overlays via the FFmpeg backend.
 *
 * v1.7.0 improvements:
 *  - Computes video hash and checks cache before uploading (2D)
 *  - Uploads in 10 MB chunks with per-chunk progress (2C)
 *  - Render job is queued server-side for parallel execution (2B)
 *
 * @returns The finished MP4/GIF blob, ready to be saved.
 */
export const exportVideoViaBackend = async (
  options: BackendExportOptions
): Promise<Blob> => {
  const {
    videoBlob,
    videoName,
    overlays,
    exportSettings,
    trimRange,
    onProgress,
  } = options;

  // Reset cancellation flag
  _cancelled = false;

  // Cancel any in-flight export first
  await cancelBackendExport();
  _cancelled = false; // reset again after cancel clears it

  // ── 0. Hash video & check cache (2D) ──────────────────────
  onProgress?.(0, 'uploading');
  const videoHash = await computeVideoHash(videoBlob);

  let uploadResult: UploadResult | null = null;

  if (videoHash) {
    const cachedPath = await checkVideoCache(videoHash);
    if (cachedPath) {
      // Cache hit — build a synthetic UploadResult so we can skip to step 2
      // We need metadata; fetch it from the backend
      try {
        const metaRes = await fetch(`${BACKEND_URL}/api/metadata`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: cachedPath }),
        });
        const metaData = await metaRes.json();
        if (metaData.success) {
          uploadResult = { path: cachedPath, name: videoName, ...metaData.data, fileSize: videoBlob.size };
          onProgress?.(10, 'preparing'); // jump straight to render
        }
      } catch { /* metadata fetch failed — proceed with upload */ }
    }
  }

  if (_cancelled) throw new Error('Export cancelled');

  // ── 1. Upload (chunked, 2C) — only if cache miss ───────────
  if (!uploadResult) {
    uploadResult = await uploadVideo(videoBlob, videoName, videoHash, onProgress);
  }

  if (_cancelled) throw new Error('Export cancelled');

  // ── 2. Start render job (queued server-side, 2B) ──────────
  onProgress?.(10, 'preparing');
  const jobId = await startRenderJob(
    uploadResult,
    rasterizePolygonOverlays(overlays),
    exportSettings,
    trimRange
  );
  _currentJobId = jobId;

  if (_cancelled) {
    await cancelBackendExport();
    throw new Error('Export cancelled');
  }

  // ── 3. Watch progress ──────────────────────────────────────
  await watchRenderProgress(jobId, onProgress);
  _currentJobId = null;

  if (_cancelled) throw new Error('Export cancelled');

  // ── 4. Download ────────────────────────────────────────────
  onProgress?.(96, 'downloading');
  const blob = await downloadResult(jobId);

  onProgress?.(100, 'complete');
  return blob;
};
